import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './homepage/components/Navbar';
import Footer from './homepage/components/Footer';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

interface FormData {
  name: string;
  email: string;
  category: string;
  priority: string;
  subject: string;
  message: string;
}

const CATEGORIES = [
  { value: '', label: 'Bitte wählen...' },
  { value: 'technical', label: 'Technisches Problem' },
  { value: 'usage', label: 'Frage zur Bedienung' },
  { value: 'billing', label: 'Abrechnung & Abo' },
  { value: 'feature', label: 'Feature-Wunsch' },
  { value: 'general', label: 'Allgemeine Anfrage' },
];

const PRIORITIES = [
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'Hoch' },
  { value: 'critical', label: 'Kritisch' },
];

const FAQ_LINKS = [
  { label: 'Wie richte ich mein Konto ein?', slug: 'erste-schritte' },
  { label: 'Wie erstelle ich eine Rechnung?', slug: 'rechnungen-erstellen' },
  { label: 'Wie lade ich Klienten zum Portal ein?', slug: 'portal-einladung' },
  { label: 'Wie verbinde ich Google Drive?', slug: 'google-drive' },
];

const inputStyle: React.CSSProperties = {
  fontFamily: "'Inter', -apple-system, sans-serif",
  fontSize: 14,
  width: '100%',
  padding: '12px 16px',
  borderRadius: 10,
  border: '1px solid #d1d5db',
  background: '#fff',
  color: '#0f172a',
  outline: 'none',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  boxSizing: 'border-box' as const,
};

const labelStyle: React.CSSProperties = {
  fontFamily: "'Inter', -apple-system, sans-serif",
  fontSize: 13,
  fontWeight: 600,
  color: '#374151',
  display: 'block',
  marginBottom: 6,
};

export default function KontaktPage() {
  const [form, setForm] = useState<FormData>({
    name: '', email: '', category: '', priority: 'normal', subject: '', message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const res = await axios.post(`${API_BASE}/public/support-ticket`, {
        name: form.name,
        email: form.email,
        category: form.category,
        priority: form.priority,
        subject: form.subject,
        message: form.message,
      });
      setSuccess(res.data?.ticket_number || 'TKT-' + Date.now().toString(36).toUpperCase());
    } catch (err: any) {
      const msg = err?.response?.data?.detail;
      setError(typeof msg === 'string' ? msg : 'Das Ticket konnte nicht erstellt werden. Bitte versuchen Sie es später erneut.');
    } finally {
      setSubmitting(false);
    }
  };

  const isValid = form.name && form.email && form.category && form.subject && form.message;

  return (
    <div className="homepage" style={{ minHeight: '100vh', background: '#f9fafb' }}>
      <Navbar />

      {/* Hero */}
      <div style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 40%, #1e3a5f 100%)',
        padding: '140px 24px 60px',
        textAlign: 'center',
      }}>
        <h1 style={{
          fontFamily: "'Plus Jakarta Sans', 'Inter', -apple-system, sans-serif",
          fontSize: 36, fontWeight: 800, color: '#fff', margin: 0,
        }}>
          Kontakt & Support
        </h1>
        <p style={{
          fontFamily: "'Inter', -apple-system, sans-serif",
          fontSize: 16, color: 'rgba(255,255,255,0.7)', marginTop: 12,
        }}>
          Wir helfen Ihnen gerne weiter
        </p>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '48px 24px 80px' }}>

        {success ? (
          /* ===== Success State ===== */
          <div style={{
            background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            padding: '56px 40px', textAlign: 'center', maxWidth: 600, margin: '0 auto',
          }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%',
              background: '#ecfdf5', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 24px',
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2 style={{
              fontFamily: "'Plus Jakarta Sans', 'Inter', -apple-system, sans-serif",
              fontSize: 24, fontWeight: 700, color: '#0f172a', margin: '0 0 8px',
            }}>
              Nachricht gesendet!
            </h2>
            <p style={{
              fontFamily: "'Inter', -apple-system, sans-serif",
              fontSize: 15, color: '#374151', margin: '0 0 24px', lineHeight: 1.6,
            }}>
              Vielen Dank für Ihre Anfrage. Wir melden uns schnellstmöglich bei Ihnen.
            </p>
            <div style={{
              background: '#f9fafb', borderRadius: 10, padding: '16px 24px',
              display: 'inline-block',
            }}>
              <p style={{
                fontFamily: "'Inter', -apple-system, sans-serif",
                fontSize: 13, color: '#6b7280', margin: '0 0 4px',
              }}>
                Ihre Ticket-Nummer
              </p>
              <p style={{
                fontFamily: "'Inter', -apple-system, sans-serif",
                fontSize: 20, fontWeight: 700, color: '#2563eb', margin: 0,
                letterSpacing: 1,
              }}>
                {success}
              </p>
            </div>
            <div style={{ marginTop: 32 }}>
              <Link to="/" style={{
                fontFamily: "'Inter', -apple-system, sans-serif",
                fontSize: 14, color: '#2563eb', textDecoration: 'none', fontWeight: 500,
              }}>
                &larr; Zurück zur Startseite
              </Link>
            </div>
          </div>
        ) : (
          /* ===== Form + Info Grid ===== */
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 360px',
            gap: 32,
          }}>
            {/* Left: Form */}
            <div style={{
              background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '36px 32px',
            }}>
              <h2 style={{
                fontFamily: "'Plus Jakarta Sans', 'Inter', -apple-system, sans-serif",
                fontSize: 20, fontWeight: 700, color: '#0f172a', margin: '0 0 24px',
              }}>
                Nachricht senden
              </h2>

              {error && (
                <div style={{
                  background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10,
                  padding: '12px 16px', marginBottom: 20,
                  fontFamily: "'Inter', -apple-system, sans-serif",
                  fontSize: 14, color: '#ef4444',
                }}>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* Name + Email Row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                  <div>
                    <label style={labelStyle}>Name *</label>
                    <input
                      name="name" value={form.name} onChange={handleChange}
                      placeholder="Ihr Name"
                      required style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>E-Mail *</label>
                    <input
                      name="email" type="email" value={form.email} onChange={handleChange}
                      placeholder="ihre@email.de"
                      required style={inputStyle}
                    />
                  </div>
                </div>

                {/* Category + Priority Row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                  <div>
                    <label style={labelStyle}>Kategorie *</label>
                    <select
                      name="category" value={form.category} onChange={handleChange}
                      required style={{ ...inputStyle, appearance: 'none' as const, cursor: 'pointer' }}
                    >
                      {CATEGORIES.map((c) => (
                        <option key={c.value} value={c.value} disabled={!c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Priorität</label>
                    <select
                      name="priority" value={form.priority} onChange={handleChange}
                      style={{ ...inputStyle, appearance: 'none' as const, cursor: 'pointer' }}
                    >
                      {PRIORITIES.map((p) => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Subject */}
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Betreff *</label>
                  <input
                    name="subject" value={form.subject} onChange={handleChange}
                    placeholder="Worum geht es?"
                    required style={inputStyle}
                  />
                </div>

                {/* Message */}
                <div style={{ marginBottom: 24 }}>
                  <label style={labelStyle}>Nachricht *</label>
                  <textarea
                    name="message" value={form.message} onChange={handleChange}
                    placeholder="Beschreiben Sie Ihr Anliegen..."
                    required rows={6}
                    style={{ ...inputStyle, resize: 'vertical' as const, lineHeight: 1.6 }}
                  />
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={submitting || !isValid}
                  style={{
                    fontFamily: "'Inter', -apple-system, sans-serif",
                    fontSize: 15, fontWeight: 600,
                    color: '#fff',
                    background: submitting || !isValid
                      ? '#93c5fd'
                      : 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                    border: 'none', borderRadius: 10,
                    padding: '14px 32px', cursor: submitting || !isValid ? 'not-allowed' : 'pointer',
                    width: '100%',
                    transition: 'opacity 0.2s',
                  }}
                >
                  {submitting ? 'Wird gesendet...' : 'Nachricht senden'}
                </button>
              </form>
            </div>

            {/* Right: Info Sidebar */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* Contact Channels */}
              <div style={{
                background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '28px 24px',
              }}>
                <h3 style={{
                  fontFamily: "'Plus Jakarta Sans', 'Inter', -apple-system, sans-serif",
                  fontSize: 16, fontWeight: 700, color: '#0f172a', margin: '0 0 20px',
                }}>
                  Weitere Kontaktwege
                </h3>

                {/* Email */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 20 }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 10,
                    background: '#eff6ff', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="4" width="20" height="16" rx="2" />
                      <polyline points="22,7 12,13 2,7" />
                    </svg>
                  </div>
                  <div>
                    <p style={{
                      fontFamily: "'Inter', -apple-system, sans-serif",
                      fontSize: 14, fontWeight: 600, color: '#0f172a', margin: '0 0 2px',
                    }}>
                      E-Mail
                    </p>
                    <a href="mailto:service@coachdesk365.com" style={{
                      fontFamily: "'Inter', -apple-system, sans-serif",
                      fontSize: 13, color: '#2563eb', textDecoration: 'none',
                    }}>
                      service@coachdesk365.com
                    </a>
                  </div>
                </div>

                {/* In-App Chat */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 20 }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 10,
                    background: '#ecfdf5', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                  </div>
                  <div>
                    <p style={{
                      fontFamily: "'Inter', -apple-system, sans-serif",
                      fontSize: 14, fontWeight: 600, color: '#0f172a', margin: '0 0 2px',
                    }}>
                      In-App Chat
                    </p>
                    <p style={{
                      fontFamily: "'Inter', -apple-system, sans-serif",
                      fontSize: 13, color: '#6b7280', margin: 0,
                    }}>
                      Direkt in der Plattform unter Nachrichten
                    </p>
                  </div>
                </div>

                {/* Help Center */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 10,
                    background: '#fef3c7', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                  </div>
                  <div>
                    <p style={{
                      fontFamily: "'Inter', -apple-system, sans-serif",
                      fontSize: 14, fontWeight: 600, color: '#0f172a', margin: '0 0 2px',
                    }}>
                      Help Center
                    </p>
                    <Link to="/hilfe" style={{
                      fontFamily: "'Inter', -apple-system, sans-serif",
                      fontSize: 13, color: '#2563eb', textDecoration: 'none',
                    }}>
                      22 Hilfe-Artikel durchsuchen
                    </Link>
                  </div>
                </div>
              </div>

              {/* FAQ Links */}
              <div style={{
                background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '28px 24px',
              }}>
                <h3 style={{
                  fontFamily: "'Plus Jakarta Sans', 'Inter', -apple-system, sans-serif",
                  fontSize: 16, fontWeight: 700, color: '#0f172a', margin: '0 0 16px',
                }}>
                  Häufige Fragen
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {FAQ_LINKS.map((faq) => (
                    <Link
                      key={faq.slug}
                      to={`/hilfe/${faq.slug}`}
                      style={{
                        fontFamily: "'Inter', -apple-system, sans-serif",
                        fontSize: 14, color: '#374151', textDecoration: 'none',
                        padding: '10px 14px', borderRadius: 8,
                        background: '#f9fafb', border: '1px solid #f3f4f6',
                        transition: 'background 0.2s, border-color 0.2s',
                        display: 'block',
                      }}
                    >
                      {faq.label}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Response Time */}
              <div style={{
                background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                borderRadius: 16, padding: '24px',
                border: '1px solid #bfdbfe',
              }}>
                <p style={{
                  fontFamily: "'Inter', -apple-system, sans-serif",
                  fontSize: 14, fontWeight: 600, color: '#1e40af', margin: '0 0 8px',
                }}>
                  Antwortzeit
                </p>
                <p style={{
                  fontFamily: "'Inter', -apple-system, sans-serif",
                  fontSize: 13, color: '#1e40af', margin: 0, lineHeight: 1.6, opacity: 0.8,
                }}>
                  Wir antworten in der Regel innerhalb von 24 Stunden an Werktagen.
                  Bei kritischen Problemen priorisieren wir Ihre Anfrage.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Responsive style override for mobile */}
        <style>{`
          @media (max-width: 768px) {
            /* Stack the grid on mobile */
            div[style*="gridTemplateColumns: '1fr 360px'"],
            div[style*="grid-template-columns"] {
              grid-template-columns: 1fr !important;
            }
          }
        `}</style>
      </div>

      <Footer />
    </div>
  );
}
